import React, { useState, useRef } from "react";
import {
  Box,
  Grid,
  Card,
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  makeStyles,
  TextField,
  Button,
  CircularProgress,
} from "@material-ui/core";
import { Lock, LockOpen } from '@material-ui/icons';
import { IoIosArrowUp, IoIosArrowDown, IoMdCheckmark } from "react-icons/io";
import * as dateFNS from "date-fns";
import { formatValue } from "react-currency-input-field";
import { toast } from "react-toastify";
import { PDFDocument } from "pdf-lib";

import useBoletosList, { BOLETO_FILTERS } from "./useBoletosList";
import { producaov2 } from "../../../../../../services/apis";
import { getNodeToken } from "../../../../../../services/auth_node";

const formatPrice = (price) => {
  return formatValue({
    value: `${price}`,
    groupSeparator: ".",
    decimalSeparator: ",",
    prefix: "R$",
    decimalScale: 2
  });
};

const useBoletoDetailsStyle = makeStyles({
  success: {
    color: "#5cb85c",
    fontWeight: 500,
  },
  error: {
    fontWeight: 500,
  },
});

function BoletoDetails({
  total = 0,
  totalPaid = 0,
  totalPending = 0,
  filters = {},
  onSubmitFilter = () => {},
  onCleanFilter = () => {},
}) {
  const formRef = useRef();
  const styles = useBoletoDetailsStyle();

  const handleSubmit = (e) => {
    e.preventDefault();

    const formData = new FormData(e.target);
    const formValues = Object.fromEntries(formData);

    onSubmitFilter(formValues);
  };

  const handleCleanFilter = () => {
    onCleanFilter();
    formRef?.current?.reset();
  };

  const hasFilters = Object.values(filters).filter(Boolean).length > 0;

  return (
    <Box my={4}>
      <Card variant="outlined">
        <Box p={2}>
          <Grid container spacing={2} justify="space-between" direction="column">
            <Grid container item spacing={8} xs={6}>
              <Grid item>
                <Typography variant="subtitle2">Total: </Typography>
                <Typography>{formatPrice(total)}</Typography>
              </Grid>
              <Grid item>
                <Typography variant="subtitle2" className={styles.success}>
                  Total Pago:{" "}
                </Typography>
                <Typography className={styles.success}>
                  {formatPrice(totalPaid)}
                </Typography>
              </Grid>
              <Grid item>
                <Typography
                  variant="subtitle2"
                  color="error"
                  className={styles.error}
                >
                  Total Pendente:{" "}
                </Typography>
                <Typography color="error" className={styles.error}>
                  {formatPrice(totalPending)}
                </Typography>
              </Grid>
            </Grid>
            <Grid
              component="form"
              container
              item
              spacing={3}
              alignItems="center"
              onSubmit={handleSubmit}
              ref={formRef}
            >
              <Grid item xs>
                <TextField
                  key={filters[BOLETO_FILTERS.USER]}
                  defaultValue={filters[BOLETO_FILTERS.USER]}
                  name="user"
                  label="Aluno"
                  fullWidth
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              <Grid item xs>
                <TextField
                  key={filters[BOLETO_FILTERS.PRODUCT]}
                  defaultValue={filters[BOLETO_FILTERS.PRODUCT]}
                  name="product"
                  label="Produto"
                  fullWidth
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              <Grid item>
                <TextField
                  key={filters[BOLETO_FILTERS.INITIAL_DATE]}
                  defaultValue={filters[BOLETO_FILTERS.INITIAL_DATE]}
                  name="initialDate"
                  label="Período inicial"
                  type="date"
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              <Grid item>
                <TextField
                  key={filters[BOLETO_FILTERS.FINAL_DATE]}
                  defaultValue={filters[BOLETO_FILTERS.FINAL_DATE]}
                  name="finalDate"
                  label="Período final"
                  type="date"
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              <Grid item>
                <Button variant="contained" color="primary" type="submit">
                  Buscar
                </Button>
              </Grid>
              {hasFilters && (
                <Grid item>
                  <Button
                    variant="outlined"
                    color="secondary"
                    type="button"
                    onClick={handleCleanFilter}
                  >
                    Limpar
                  </Button>
                </Grid>
              )}
            </Grid>
          </Grid>
        </Box>
      </Card>
    </Box>
  );
}

const downloadFile = (file) => {
  const anchorElement = document.createElement("a");
  anchorElement.style.display = "none";
  anchorElement.href = URL.createObjectURL(file);
  anchorElement.download = file.name;

  document.body.appendChild(anchorElement);
  anchorElement.click();

  setTimeout(() => {
    URL.revokeObjectURL(anchorElement.href);
    anchorElement.parentNode.removeChild(anchorElement);
  }, 0);
};

function BoletoTableRow({ boleto }) {
  const [isOpen, setIsOpen] = useState(false);
  const [loadingMail, setLoadingMail] = useState(false);
  const [boletoIDSentMail, setBoletoIDSentMail] = useState([]);
  const [loadingJoinBoletos, setLoadingJoinBoletos] = useState(false);
  const [hasAccessBlocking, setHasAccessBlocking] = useState(boleto?.hasAccessBlocking || false);
  const [loadingBlocking, setLoadingBlocking] = useState(false);

  const statusMapper = {
    pending: "Pendente",
    paid: "Pago",
    failed: "Falha",
    canceled: "Estornado",
  };

  const handleSendMail = async (boletoID) => {
    try {
      setLoadingMail(true);

      await producaov2.post(`/admin/boletos/${boletoID}/send-mail`);

      setBoletoIDSentMail((prev) => [...new Set([...prev, boletoID])]);

      toast.success("Boleto enviado com sucesso para o email");
    } catch (error) {
      console.log(error);

      toast.error("Houve um erro ao enviar o boleto");
    } finally {
      setLoadingMail(false);
    }
  };

  const handleDownloadBoletos = async () => {
    try {
      setLoadingJoinBoletos(true);

      const boletosFiles = await Promise.all(
        boleto?.boletos?.map(async (boletoItem) => {
          const fileURL = `${producaov2.defaults.baseURL}admin/boletos/pdf?boletoURL=${boletoItem?.url}&boletoID=${boletoItem?.id}`;

          const boletoPDFInBytes = await fetch(fileURL, {
            headers: {
              access_token: `voitto-token ${getNodeToken()}`,
            }
          }).then((res) =>
            res.arrayBuffer()
          );

          return {
            id: boletoItem?.extras?.installment,
            pdfBytes: boletoPDFInBytes,
          };
        })
      );

      const ordenedBoletoFiles = boletosFiles.sort(
        (boletoFileA, boletoFileB) => boletoFileA.id - boletoFileB.id
      );

      const pdfDoc = await PDFDocument.create();

      // Usando assim para salvar os boletos na ordem
      for (const boletoFile of ordenedBoletoFiles) {
        const [embedPDF] = await pdfDoc.embedPdf(boletoFile.pdfBytes);

        const page = pdfDoc.addPage();
        page.drawPage(embedPDF);
      }

      const pdfData = await pdfDoc.save();
      const pdfBlob = new Blob([pdfData], { type: "application/pdf" });
      const pdfFile = new File([pdfBlob], "Boletos.pdf");

      downloadFile(pdfFile);
    } catch (error) {
      console.log(error);
      toast.error("Houve um erro ao juntar os boletos, contate TI");
    } finally {
      setLoadingJoinBoletos(false);
    }
  };

  const handleAccessBlocking = async () => {
    try {
      setLoadingBlocking(true);

      const response = hasAccessBlocking
        ? await producaov2.delete('/admin/access-blocking', {
          params: {
            userID: boleto?.id_pessoa,
            courseID: boleto?.id_curso,
            reasonID: 3,
          }
        })
        : await producaov2.post('/admin/access-blocking', {
          userID: boleto?.id_pessoa,
          courseID: boleto?.id_curso,
          reasonID: 3,
          message: 'Bloqueio de conteúdo por existir boletos pendentes de pagamento'
        });

      setHasAccessBlocking(prev => !prev);

      toast.success(`${hasAccessBlocking ? 'Desbloqueado' : 'Bloqueado'} com sucesso`);
    } catch(error) {
      console.log(error);

      toast.error('Houve um erro ao bloquear o acesso');
    } finally {
      setLoadingBlocking(false);
    }
  }

  const loadingBlockingText = hasAccessBlocking ? "Desbloqueando..." : "Bloqueando...";
  const blockingText = hasAccessBlocking ? "Desbloquear acesso" : "Bloquear acesso";

  return (
    <>
      <TableRow key={boleto?.id}>
        <TableCell>
          <IconButton size="small" onClick={() => setIsOpen((prev) => !prev)}>
            {isOpen ? (
              <IoIosArrowUp color="#000" size="1em" />
            ) : (
              <IoIosArrowDown color="#000" size="1em" />
            )}
          </IconButton>
        </TableCell>
        <TableCell>{boleto?.id}</TableCell>
        <TableCell>
          <p>{boleto?.user_name}</p>
          <p>{boleto?.user_email}</p>
        </TableCell>
        <TableCell>{boleto?.product_name}</TableCell>
        <TableCell>{formatPrice(boleto?.price)}</TableCell>
        <TableCell>{boleto?.installments}</TableCell>
        <TableCell>
          {boleto?.created_at
            ? dateFNS.format(new Date(boleto?.created_at), "dd/MM/yyyy")
            : ""}
        </TableCell>
        <TableCell>
          <Button
            disabled={loadingJoinBoletos}
            variant="outlined"
            color="primary"
            size="small"
            onClick={handleDownloadBoletos}
          >
            {loadingJoinBoletos ? "Juntando Boletos" : "Baixar Boletos"}
          </Button>
        </TableCell>
        <TableCell>
          {boleto?.id_pessoa && boleto?.id_curso && (
            <Button
              disabled={loadingBlocking}
              variant="text"
              color="primary"
              size="small"
              onClick={handleAccessBlocking}
            >
              {hasAccessBlocking ? <Lock /> : <LockOpen />}
              {loadingBlocking ? loadingBlockingText : blockingText}
            </Button>
          )}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={9}>
          <Collapse in={isOpen} timeout="auto" unmountOnExit>
            <Box margin={1}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Status</TableCell>
                    <TableCell>Valor</TableCell>
                    <TableCell>Parcela</TableCell>
                    <TableCell>Link</TableCell>
                    <TableCell>Instruções</TableCell>
                    <TableCell>Observação</TableCell>
                    <TableCell>Vencimento</TableCell>
                    <TableCell>Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {boleto?.boletos?.map((boletoItem) => {
                    const hasSentMail =
                      boletoItem.sent_mail ||
                      boletoIDSentMail?.includes(boletoItem.id);

                    const mailButtonText = hasSentMail
                      ? "Enviado"
                      : "Enviar Email";

                    return (
                      <TableRow key={boletoItem?.id}>
                        <TableCell>
                          {statusMapper[boletoItem?.status]}
                        </TableCell>
                        <TableCell>{formatPrice(boletoItem?.price)}</TableCell>
                        <TableCell>{boletoItem?.extras?.installment}</TableCell>
                        <TableCell style={{ maxWidth: '200px', overflowX: 'hidden', textOverflow: 'ellipsis' }}>
                          <a href={boletoItem?.url} target="_blank">
                            {boletoItem?.url}
                          </a>
                        </TableCell>
                        <TableCell>{boletoItem?.instructions}</TableCell>
                        <TableCell>{boletoItem?.notes}</TableCell>
                        <TableCell>
                          {boletoItem?.due_at
                            ? dateFNS.format(
                                new Date(boletoItem?.due_at),
                                "dd/MM/yyyy"
                              )
                            : ""}
                        </TableCell>
                        <TableCell>
                          <Button
                            size="small"
                            variant="outlined"
                            color={hasSentMail ? "secondary" : "primary"}
                            onClick={() => handleSendMail(boletoItem.id)}
                            disabled={loadingMail}
                            endIcon={
                              hasSentMail ? (
                                <IoMdCheckmark size="0.8em" />
                              ) : null
                            }
                            title={
                              hasSentMail
                                ? "Email do boleto enviado para comprador"
                                : "Enviar boleto por email para comprador"
                            }
                          >
                            {loadingMail ? (
                              <CircularProgress size={20} />
                            ) : (
                              mailButtonText
                            )}
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

const useStyles = makeStyles(() => ({
  table: {
    overflowX: 'auto'
  },
  body: {
    position: "relative",
  },
  emptyBody: {
    height: "300px",
  },
  loadingOverlay: {
    backgroundColor: "rgba(255, 255, 255, 0.5)",
    backdropFilter: "blur(5px)",
    position: "absolute",
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
}));

export default function BoletosList() {
  const classnames = useStyles();

  const {
    data,
    isLoading,
    total,
    page,
    limit,
    boletosDetail,
    filter,
    onChangeLimit,
    onChangePage,
    onSubmitFilter,
    onCleanFilter,
  } = useBoletosList();

  const handleChangeRowsPerPage = (event) => {
    onChangeLimit(parseInt(event.target.value, 10));
    onChangePage(1);
  };

  const handleChangePage = (event, newPage) => {
    onChangePage(newPage + 1);
  };

  return (
    <>
      <BoletoDetails
        {...boletosDetail}
        filters={filter}
        onCleanFilter={onCleanFilter}
        onSubmitFilter={onSubmitFilter}
      />
      <Table className={classnames.table}>
        <TableHead>
          <TableRow>
            <TableCell />
            <TableCell>ID</TableCell>
            <TableCell>Comprador</TableCell>
            <TableCell>Produto</TableCell>
            <TableCell>Valor</TableCell>
            <TableCell>Parcelas</TableCell>
            <TableCell>Criado em</TableCell>
            <TableCell />
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody
          className={`${classnames.body} ${
            data?.length === 0 && classnames.emptyBody
          }`}
        >
          {isLoading && (
            <Box className={classnames.loadingOverlay}>
              <Typography variant="subtitle1">Carregando ...</Typography>
            </Box>
          )}
          {data?.map((boleto) => {
            return <BoletoTableRow boleto={boleto} />;
          })}
        </TableBody>
        <TableFooter>
          <TableRow>
            {!!total && (
              <TablePagination
                rowsPerPageOptions={[10, 30, 60, 100]}
                colSpan={9}
                count={total}
                rowsPerPage={limit}
                page={page - 1}
                onChangePage={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            )}
          </TableRow>
        </TableFooter>
      </Table>
    </>
  );
}
